import dayjs from "dayjs";
import { Link } from "react-router";
import {
  BonificoExternalSettlementDTO,
  BonificoInstallmentsSettlementDTO,
  ExternalOrderPaymentType,
  ExternalSettlementGateway,
  externalSettlementGatewayLabel,
  GATEWAYS_SELLING_CATALOG_PRODUCTS,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { toStripeOrderDetailsPath } from "../Stripe/stripeOrderDetailsPath.ts";

function formatEuros(money: { amount: number }): string {
  return `€${(money.amount / 100).toFixed(2)}`;
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="font-medium text-gray-700">{label}:</span>
      <p className="text-gray-600">{value}</p>
    </div>
  );
}

export function InstallmentsSettlementDetails({
  settlement,
  settlementAmount,
}: {
  settlement: BonificoInstallmentsSettlementDTO;
  settlementAmount?: { amount: number };
}) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div className="col-span-2">
        <span className="font-medium text-gray-700">Ordine Stripe:</span>
        <p>
          <Link
            className="font-mono text-xs text-blue-600 hover:underline"
            to={toStripeOrderDetailsPath(settlement.gatewayOrderId)}
          >
            {settlement.gatewayOrderId}
          </Link>
        </p>
      </div>
      <DetailRow
        label="Piano"
        value={`${settlement.numberOfInstallments} rate`}
      />
      <DetailRow
        label="Totale ordine"
        value={formatEuros(settlement.totalAmountInCents)}
      />
      <DetailRow
        label="Già pagato su Stripe"
        value={formatEuros(settlement.amountAlreadyPaidInCents)}
      />
      {settlementAmount && (
        <DetailRow
          label="Saldo con bonifico"
          value={formatEuros(settlementAmount)}
        />
      )}
      <DetailRow
        label="Saldo registrato il"
        value={dayjs(settlement.settledAtUtc).format("DD/MM/YYYY HH:mm")}
      />
    </div>
  );
}

function originalPaymentDescription(
  settlement: BonificoExternalSettlementDTO,
): string {
  if (settlement.originalPaymentType === ExternalOrderPaymentType.OneTime)
    return "Soluzione unica";
  return settlement.numberOfInstallments
    ? `A rate (${settlement.numberOfInstallments} rate)`
    : "A rate";
}

export function ExternalSettlementDetails({
  settlement,
  settlementAmount,
}: {
  settlement: BonificoExternalSettlementDTO;
  settlementAmount?: { amount: number };
}) {
  const gatewayLabel = externalSettlementGatewayLabel(settlement);
  const isOneTime =
    settlement.originalPaymentType === ExternalOrderPaymentType.OneTime;
  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <span className="font-medium text-gray-700">
          Ordine {gatewayLabel}:
        </span>
        {settlement.gateway === ExternalSettlementGateway.Stripe ? (
          <p>
            <Link
              className="font-mono text-xs text-blue-600 hover:underline break-all"
              to={toStripeOrderDetailsPath(settlement.externalOrderId)}
            >
              {settlement.externalOrderId}
            </Link>
          </p>
        ) : (
          <p className="font-mono text-xs text-gray-600 break-all">
            {settlement.externalOrderId}
          </p>
        )}
      </div>
      {!GATEWAYS_SELLING_CATALOG_PRODUCTS.includes(settlement.gateway) && (
        <DetailRow
          label={`Prodotto ${gatewayLabel}`}
          value={settlement.externalProductId || "Non indicato"}
        />
      )}
      <DetailRow
        label="Pagamento originale"
        value={originalPaymentDescription(settlement)}
      />
      <DetailRow
        label="Totale ordine"
        value={formatEuros(settlement.totalAmountInCents)}
      />
      <DetailRow
        label={`Già pagato su ${gatewayLabel}`}
        value={formatEuros(settlement.amountAlreadyPaidInCents)}
      />
      {settlementAmount && (
        <DetailRow
          label={isOneTime ? "Integrazione con bonifico" : "Saldo con bonifico"}
          value={formatEuros(settlementAmount)}
        />
      )}
      <p className="col-span-2 text-xs text-gray-500">
        {isOneTime
          ? `Inserito a mano: su ${gatewayLabel} non viene aggiornato nulla.`
          : `Inserito a mano: la subscription su ${gatewayLabel} va gestita direttamente lì.`}
      </p>
    </div>
  );
}
